import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AnyAction, Store } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';

import { cn } from 'helpers/classnames';
import { SUCCESS } from 'constants/launchCalendar';
import { titaniteView, track } from 'apis/amethyst';
import { evLaunchCalendarPageView, evLaunchCalendarSectionClick, LAUNCH_CALENDAR_SECTION_TYPE } from 'events/launchCalendar';
import useEffectOnce from 'hooks/useEffectOnce';
import { fetchLaunchCalendarRecentLaunches, fetchLaunchCalendarUpcomingStyles, fetchProductDetails } from 'actions/launchCalendar';
import { selectLaunchCalendar } from 'selectors/launchCalendar';
import { LaunchCalendarSection } from 'components/launchcalendar/LaunchCalendarSection';
import { isAssigned, triggerAssignment } from 'actions/ab';
import { HYDRA_LAUNCH_CALENDAR } from 'constants/hydraTests';
import { getLCRecentLaunchesInfo, getLCUpcomingProductInfo } from 'components/launchcalendar/LaunchCalendar.utility';
import { LaunchCalendarSectionType, LaunchCalendarTabs } from 'types/launchCalendar';
import type { AppState } from 'types/app';
import type { AppDispatch } from 'entrypoints/bootstrapOnClient';
import { trackError } from 'helpers/ErrorUtils';

import css from 'styles/containers/launchCalendar.scss';

export const LaunchCalendar = () => {
  const dispatch: AppDispatch = useDispatch();
  const { upcomingStyles, recentLaunches, apiStatus } = useSelector(selectLaunchCalendar);
  const isLaunchCalendarHydraAssigned = useSelector(state => isAssigned(HYDRA_LAUNCH_CALENDAR, 1, state));
  const [activeTab, setActiveTab] = useState<LaunchCalendarTabs>(LaunchCalendarTabs.UPCOMING);

  useEffectOnce(() => {
    titaniteView();
    dispatch(triggerAssignment(HYDRA_LAUNCH_CALENDAR));
  });

  useEffect(() => {
    if (isLaunchCalendarHydraAssigned) {
      dispatch(fetchLaunchCalendarUpcomingStyles());
      dispatch(fetchLaunchCalendarRecentLaunches());
    }
  }, [isLaunchCalendarHydraAssigned]);

  const { upcomingLaunches, todayLaunches } = useMemo(() => getLCUpcomingProductInfo(upcomingStyles), [upcomingStyles]);
  const recentlyLaunched = useMemo(() => getLCRecentLaunchesInfo(recentLaunches), [recentLaunches]);

  useEffect(() => {
    const baseOpts = {
      includeOos: true
    };

    todayLaunches.map(product => {
      const fetchOpts = {
        ...baseOpts,
        productId: String(product.productId)
      };
      dispatch(fetchProductDetails(fetchOpts));
    });
  }, [todayLaunches]);

  useEffect(() => {
    if (apiStatus === SUCCESS) {
      track(() => [evLaunchCalendarPageView, { itemsFound: todayLaunches.length + upcomingLaunches.length }]);
    }
  }, [apiStatus]);

  const createUpcomingLaunchesSection = () => (
    <>
      {todayLaunches.length > 0 && (
        <LaunchCalendarSection heading="Today's Releases" products={todayLaunches} sectionType={LaunchCalendarSectionType.TODAYS_RELEASES} />
      )}
      {upcomingLaunches.length > 0 && (
        <LaunchCalendarSection heading="Upcoming Releases" products={upcomingLaunches} sectionType={LaunchCalendarSectionType.UPCOMING_RELEASES} />
      )}
    </>
  );

  const createRecentLaunchesSection = () => (
    <>
      {recentlyLaunched.length > 0 && (
        <LaunchCalendarSection heading="Recent Releases" products={recentlyLaunched} sectionType={LaunchCalendarSectionType.RECENT_LAUNCHES} />
      )}
    </>
  );

  const handleUpcomingLaunchesTabClick = () => {
    setActiveTab(LaunchCalendarTabs.UPCOMING);
    track(() => [
      evLaunchCalendarSectionClick,
      { sectionType: LAUNCH_CALENDAR_SECTION_TYPE.UpcomingLaunches, itemsFound: todayLaunches.length + upcomingLaunches.length }
    ]);
  };

  const handleRecentLaunchesTabClick = () => {
    setActiveTab(LaunchCalendarTabs.RECENTLY_LAUNCHED);
    track(() => [evLaunchCalendarSectionClick, { sectionType: LAUNCH_CALENDAR_SECTION_TYPE.RecentLaunches, itemsFound: recentlyLaunched.length }]);
  };

  return (
    <>
      {isLaunchCalendarHydraAssigned && (
        <div className={css.launchCalendarTab}>
          <div className={css.nav}>
            <button
              type="button"
              className={cn(css.heading, { [css.active]: activeTab === LaunchCalendarTabs.UPCOMING })}
              onClick={handleUpcomingLaunchesTabClick}
            >
              Upcoming ({upcomingLaunches.length + todayLaunches.length})
            </button>
            <button
              type="button"
              className={cn(css.heading, { [css.active]: activeTab === LaunchCalendarTabs.RECENTLY_LAUNCHED })}
              onClick={handleRecentLaunchesTabClick}
            >
              Recently Launched ({recentlyLaunched.length})
            </button>
          </div>
          <div className={css.mainCard}>
            {activeTab === LaunchCalendarTabs.UPCOMING ? createUpcomingLaunchesSection() : createRecentLaunchesSection()}
          </div>
        </div>
      )}
    </>
  );
};

LaunchCalendar.fetchDataOnServer = (store: Store) => {
  const { dispatch } = store as {
    dispatch: ThunkDispatch<AppState, void, AnyAction>;
  };
  const { getState } = store;
  return Promise.all([dispatch(fetchLaunchCalendarUpcomingStyles()), dispatch(fetchLaunchCalendarRecentLaunches())])
    .then(() => {
      const {
        launchCalendar: { upcomingStyles }
      } = getState();
      const { todayLaunches } = getLCUpcomingProductInfo(upcomingStyles);
      const baseOpts = {
        includeOos: true
      };
      Promise.all(
        todayLaunches.map(product => {
          const fetchOpts = {
            ...baseOpts,
            productId: String(product.productId)
          };
          dispatch(fetchProductDetails(fetchOpts));
        })
      );
    })
    .catch(err => {
      trackError('ERROR', 'Could not get Launch Calendar details', err);
    });
};

export default LaunchCalendar;
